<script setup lang="ts">
import { Form } from 'ant-design-vue';
import { debounce, get, isEqual } from 'lodash-es';
import { reactive, watch } from 'vue';
import { request } from '@/service/request';

defineOptions({
  name: 'MySelect'
});

interface MySelectProps {
  api?: string;
  value?: any;
  // 请求体中
  searchParams?: Record<string, any>;
  // url中
  queryParams?: Record<string, any>;
  dataKey?: string | string[];
  requestMethod?: 'get' | 'post';
  labelKey?: string | string[];
  valueKey?: string | string[];
  onResponse?: (response: Record<string, any>) => any[];
  labelInValue?: boolean;
  searchKey?: string;
  isRemoteSearch?: boolean;
}

const {
  api,
  searchParams,
  queryParams,
  dataKey = ['data', 'results'],
  requestMethod = 'get',
  onResponse,
  labelKey = 'name',
  valueKey = 'id',
  value,
  labelInValue = false,
  isRemoteSearch = false,
  searchKey = 'name'
} = defineProps<MySelectProps>();

const { onFieldChange } = Form.useInjectFormItemContext();
const dataSource = reactive<any>({
  data: [],
  options: [],
  loading: false
});
const emit = defineEmits<{
  (e: 'update:value', selectedValue: any): void;
}>();

watch(
  [() => api, () => searchParams, () => queryParams],
  (newVal, oldVal) => {
    if (!isEqual(newVal, oldVal)) {
      getDataSource({});
    }
  },
  {
    immediate: true
  }
);

async function getDataSource(params: Record<string, any> = {}) {
  let bodyParams = {
    ...searchParams
  };
  let urlParams = {
    ...queryParams
  };
  if (requestMethod !== 'get') {
    bodyParams = {
      ...bodyParams,
      ...params
    };
  } else {
    urlParams = {
      ...urlParams,
      ...params
    };
  }
  try {
    dataSource.loading = true;
    const res = await request<any>({
      url: api,
      method: requestMethod,
      data: bodyParams,
      params: urlParams
    });
    if (onResponse) {
      const data = onResponse(res);
      dataSource.data = data;
      dataSource.options = data.map(item => {
        return {
          label: get(item, labelKey),
          value: get(item, valueKey),
          title: get(item, labelKey),
          extra: item
        };
      });
    } else {
      const d: any[] = get(res, dataKey, []);
      dataSource.data = d;
      dataSource.options = d.map(item => {
        return {
          label: get(item, labelKey),
          value: get(item, valueKey),
          title: get(item, labelKey),
          extra: item
        };
      });
    }
  } catch (error) {
    console.log(error);
    dataSource.data = [];
    dataSource.options = [];
  } finally {
    dataSource.loading = false;
  }
}

function onChange(val: any) {
  emit('update:value', val);
  onFieldChange?.();
}

function handleSearch(val: string) {
  if (isRemoteSearch) {
    debounce(() => {
      getDataSource({ [searchKey]: val });
    }, 300)();
  }
}
</script>

<template>
  <ASelect
    :value="value"
    :loading="dataSource.loading"
    :options="dataSource.options"
    :label-in-value="labelInValue"
    :filter-option="!isRemoteSearch"
    @change="onChange"
    @search="handleSearch"
  >
    <template v-if="dataSource.loading" #notFoundContent>
      <ASpin size="small" />
    </template>
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData"></slot>
    </template>
  </ASelect>
</template>

<style lang="scss" scoped></style>
